/**
 * Anchoring a bare calendar-day string to a concrete instant before it reaches
 * a `timestamptz` column — the general form of the fix from issue #2520.
 *
 * Originally written as `anchorTaskDueDate` in `task-due-date.ts` for
 * `contact_tasks.due_date`. Issue #2545 found the SAME defect on
 * `tickets.due_date` (`apps/hub/lib/core/chat/tools/executors/edit/tickets.ts`,
 * behind Slack's ticket-edit flexpane AND the iOS/desktop workspace-addon
 * `PATCH /api/workspace-addon/crm/tickets/[id]` route — both feed the same
 * `executeUpdateTicket`, so anchoring there fixes every client at once per
 * `NATIVE_CLIENTS_SHARE_ONE_SEAM`). For tickets the bare date reaches Postgres
 * through a DIFFERENT mechanism than the original tasks bug — pg_graphql's
 * `Datetime` scalar parser outright REJECTS a date-only string, so
 * `serializeGraphqlInput`/`toDatetimeString` (`lib/graphql/serialize-input.ts`)
 * widens it to `T00:00:00.000Z` (UTC MIDNIGHT) before the mutation. Same
 * midnight-UTC anchor, same one-Central-calendar-day-early read-back, just a
 * rejection-driven widen instead of a silent session-timezone cast. Anchoring
 * the value here, before it ever reaches `serializeGraphqlInput`, means the
 * widening branch never fires (the value is already a full timestamp) —
 * exactly mirroring how the tasks executor already anchors before its insert.
 *
 * `tickets.due_date` is `timestamp with time zone`
 * (`apps/hub/supabase/migrations/20241201000000_baseline.sql:7380`).
 *
 * This module holds the anchor PRIMITIVE, generalized beyond the tasks table
 * so a second write path never has to copy-paste it. `task-due-date.ts`
 * re-exports it under its original name (`anchorTaskDueDate`) so every
 * existing call site keeps compiling unchanged.
 *
 * Fix: anchor a bare calendar day at NOON in `America/Chicago`, not midnight —
 * the same defence iOS's `HubCalendarDate` already uses (see the comment at
 * `apps/ios/WTCHubKit/Sources/WTCHubKit/Contracts/Invoice.swift:113-115`:
 * "Noon, not midnight, so a DST shift or a viewer in another zone can never
 * slide the date onto the previous day."). Midnight anchoring is one DST hour
 * from reintroducing this exact bug.
 *
 * The actual wall-clock-to-UTC conversion is `@wtc/business-date`'s
 * `zonedWallClockToUtc` — a DST-aware, two-pass, Intl-derived-offset
 * primitive, tested directly in that package. This module used to carry its
 * OWN copy of that same two-pass algorithm (a private `centralOffsetAt` that
 * was byte-for-byte the one still living in `lib/webinars/wall-clock.ts`);
 * consolidated onto the shared package rather than kept as a second
 * implementation of the identical math. The timezone stays hardcoded to
 * `"America/Chicago"` here rather than reading `@wtc/business-date`'s
 * env-overridable `resolveBusinessTimeZone()` — this write path is what
 * `CALENDAR_DATE_ANCHORING.md` describes as anchoring at noon
 * `America/Chicago` specifically, and picking up an env override would be a
 * behavior change this consolidation is not making.
 *
 * Return type: `Instant` (`calendar-date-brand.ts`), not plain `string` — the
 * write-side half of `CALENDAR_DATE_ANCHORING`'s "make the wrong write
 * unrepresentable" goal. A write-site local variable typed against the
 * branded Insert/Update input (see `calendar-date-branded-writes.ts`) can
 * then ONLY be satisfied by a value that came through this function (or
 * `asInstant`) — a raw unanchored `params.due_date` passthrough fails to
 * compile instead of silently reaching Postgres.
 */

import { z } from "zod";
import { businessDateString, zonedWallClockToUtc } from "@west-tennessee-consulting/business-date";
import type { Instant } from "./calendar-date-brand";

const BUSINESS_TIMEZONE = "America/Chicago";

/** A bare calendar day with no time and no offset — what every client sends. */
const BARE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Brands an already-string value `Instant` WITHOUT the format validation
 * `asInstant` (`calendar-date-brand.ts`) applies. Deliberately lenient,
 * used only for this function's own return paths, both already-audited by
 * construction: the noon-anchor computation always produces a
 * `Date#toISOString()` output (always a valid full instant), and the
 * passthrough branch preserves this function's EXISTING, tested contract of
 * returning a non-bare-date input (an already-precise instant, or an
 * unparseable string) UNCHANGED rather than throwing — see the doc comment
 * below. No `as` cast (per `main-03`): `.brand()` on a bare `z.string()`
 * schema checks nothing beyond "is a string" and attaches the brand the same
 * way the CRM id brands do (`id-resolution/branded-ids.ts`). A NEW call site
 * with untrusted input should use `asInstant`, which actually validates.
 */
const brandInstantSchema = z.string().brand<"Instant">();
function brandInstant(value: string): Instant {
  return brandInstantSchema.parse(value);
}

/**
 * Anchor a bare `YYYY-MM-DD` calendar date at noon `America/Chicago`,
 * returning an ISO instant.
 *
 * A value that does not match the bare-date shape — already carries a time,
 * or is nullish — is a caller who meant something more specific than "this
 * calendar day": passed through UNTOUCHED. That covers both an already-precise
 * instant (a caller who computed one deliberately) and an unparseable string
 * (Postgres, not this function, is the right place for that to surface as an
 * error).
 *
 * The wall-clock-to-UTC conversion itself is `zonedWallClockToUtc`
 * (`@wtc/business-date`) — the same two-pass, DST-aware primitive used
 * everywhere else in the codebase, rather than a second copy of that
 * algorithm living here.
 */
export function anchorBareCalendarDate(dueDate: string): Instant;
export function anchorBareCalendarDate(dueDate: string | null): Instant | null;
export function anchorBareCalendarDate(
  dueDate: string | null | undefined
): Instant | null | undefined;
export function anchorBareCalendarDate(
  dueDate: string | null | undefined
): Instant | null | undefined {
  if (dueDate == null) return dueDate;
  if (!BARE_DATE_RE.test(dueDate)) return brandInstant(dueDate);

  const [year, month, day] = dueDate.split("-").map(Number);
  const anchored = zonedWallClockToUtc(year, month, day, 12, 0, BUSINESS_TIMEZONE);

  // A shape-valid but non-existent calendar date (month 13, day 45, …) rolls
  // over silently under `Date.UTC` rather than producing NaN — the previous
  // implementation rejected exactly this shape by relying on native ISO
  // string parsing returning Invalid Date for an out-of-range month/day.
  // Reproduce that here by confirming the anchored instant reads back as the
  // SAME Central calendar day, rather than anchoring whatever day it rolled
  // over to. This can never false-positive on a legitimate date: noon is
  // never inside a DST gap, so (unlike an arbitrary wall-clock hour) the
  // read-back always agrees with the input for anything that was actually a
  // real calendar day.
  if (businessDateString(anchored, BUSINESS_TIMEZONE) !== dueDate) {
    return brandInstant(dueDate);
  }

  return brandInstant(anchored.toISOString());
}
